import { Color, Spacing } from '@persona-id/design-tokens';
import { ZapIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import Fuse from 'fuse.js';
import { ReactNode, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalContainer, ModalFooter } from 'dashboard/layouts/ModalLayout';
import { CaseActionBuilderDecription, CaseActionBuilderName } from 'dashboard/models/case-action';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Heading, Paragraph } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import { BASE } from 'design-system/variables/spacing';
import useDebounced from 'lib/hooks/useDebounced';

const CARD_HEIGHT = 130;
const HEADER_HEIGHT = 136;

/* modal body components */
const StyledModalContainer = styled(ModalContainer)`
  position: relative;
  height: clamp(700px, 85vh, 850px);
  width: clamp(980px, 90vw, 1100px);
  padding: 0;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-gap: ${Spacing[4]};
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: ${CARD_HEIGHT}px;
  height: 100%;
  overflow-y: scroll;

  background-color: ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const NoResultsWrapper = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding-top: ${Spacing[8]};
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

/* modal header components */
const StyledModalHeader = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  height: ${HEADER_HEIGHT}px;
  z-index: 1;

  padding: ${Spacing[5]};
  width: 100%;
`;

const HeaderTitleWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledIconX = styled(XIcon)`
  cursor: pointer;
  margin-left: auto;
`;

const SearchFilterRow = styled('div')`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

/* modal card components */
const StyledCard = styled(Card)<{ selected: boolean }>`
  ${hoverableMixin}
  max-height: 240px; /* Safari fix -- auto grid row height is buggy in Safari */
  margin-bottom: 0px;
  border-width: 2px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border-color: ${({ selected }) => (selected ? Color.CorePurple800 : Color.PrimaryWhite)};
`;

const StyledCardBody = styled(Card.Body)`
  display: flex;
  flex-direction: row;
  flex: auto;
`;

const BuilderIcon = styled('div')`
  display: flex;
  width: ${Spacing[7]};
  height: ${Spacing[7]};
  padding: ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  background: ${({ theme }) => theme.color.primary.brand100};
  justify-content: center;
  align-items: center;

  svg {
    width: ${Spacing[5]};
    height: ${Spacing[5]};
    color: ${Color.CorePurple600};
  }
`;

const ContentWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacing[4]};
`;

const PageFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  height: ${BASE * 9}px;
  padding: ${Spacing[4]} ${Spacing[5]};

  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  width: 100%;
`;

const ConfirmationButton = styled(Button)`
  width: 180px;
  padding: ${Spacing[4]};
`;

interface CreateCaseActionModalProps {
  caseActionBuilders: CaseActionBuilderName[];
  handleCloseModal: () => void;
  handleCreateCaseAction: (builder?: string) => Promise<void>;
}

const CreateCaseActionModal = ({
  caseActionBuilders,
  handleCloseModal,
  handleCreateCaseAction,
}: CreateCaseActionModalProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedBuilder, setSelectedBuilder] = useState<CaseActionBuilderName>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 500, {
    leading: false,
    trailing: true,
  });

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: [
        { name: 'name', weight: 0.7 },
        { name: 'steps', weight: 0.3 },
      ],
    };
    return new Fuse(caseActionBuilders, fuseOptions);
  }, [caseActionBuilders]);

  const searchResults: Set<string> | null = useMemo(
    () => (searchTerm !== '' ? new Set(fuse.search(searchTerm).map(({ item }) => item)) : null),
    [searchTerm],
  );

  const cardsToShow = useMemo<React.ReactNode[]>(() => {
    const rows: ReactNode[] = [];
    (caseActionBuilders || []).map((builder) => {
      // skip row if search in progress and row is not included in results
      if (searchResults != null && !searchResults.has(builder)) {
        return;
      }

      const { title, description } = CaseActionBuilderDecription[builder as CaseActionBuilderName];

      const builderCard = (
        <CaseActionBuilderCard
          key={builder}
          name={builder}
          selectedBuilder={selectedBuilder}
          title={title}
          description={description}
          icon={<ZapIcon />}
          onClick={() => {
            setSelectedBuilder(builder);
          }}
        />
      );
      rows.push(builderCard);
    });
    return rows;
  }, [caseActionBuilders, searchResults, selectedBuilder]);

  return (
    <StyledModalContainer data-test='create-case-action-modal'>
      <CreateCaseActionModalHeader
        searchTerm={searchTerm}
        handleSetSearchTerm={(searchTerm) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          setSelectedBuilder(null);
          debouncedSetSearchTerm(searchTerm);
        }}
        handleCloseModal={handleCloseModal}
      />
      <SelectBuilderView cardsToShow={cardsToShow} />
      <CreateCaseActionModalFooter
        selectedBuilder={selectedBuilder}
        handleCreateCaseAction={handleCreateCaseAction}
      />
    </StyledModalContainer>
  );
};

const SelectBuilderView = ({ cardsToShow }: { cardsToShow: ReactNode[] }) => {
  return cardsToShow.length > 0 ? (
    <CardsContainer>{cardsToShow}</CardsContainer>
  ) : (
    <NoResultsWrapper>
      <Paragraph size='md'>No matched results. Try changing your search.</Paragraph>
    </NoResultsWrapper>
  );
};

interface CreateCaseActionModalHeaderProps {
  searchTerm: string;
  handleSetSearchTerm: (searchTerm: string) => void;
  handleCloseModal: () => void;
}

const CreateCaseActionModalHeader = ({
  searchTerm,
  handleSetSearchTerm,
  handleCloseModal,
}: CreateCaseActionModalHeaderProps) => {
  return (
    <StyledModalHeader>
      <HeaderTitleWrapper>
        <Heading size='sm'>Select from library</Heading>
        <StyledIconX data-test='close-modal' onClick={handleCloseModal} />
      </HeaderTitleWrapper>
      <SearchFilterRow>
        <SearchInput
          id='case-actions-template__search-input'
          dataTest='case-actions-template__search-input'
          placeholder='Search'
          handleInput={handleSetSearchTerm}
          value={searchTerm}
          title={null}
        />
      </SearchFilterRow>
    </StyledModalHeader>
  );
};

interface CaseActionBuilderCardProps {
  selectedBuilder: string;
  name: string;
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
}

const CaseActionBuilderCard = ({
  selectedBuilder,
  name,
  title,
  description,
  icon,
  onClick,
}: CaseActionBuilderCardProps) => {
  return (
    <StyledCard
      data-test={`create-case-action-modal__card-${name}`}
      selected={selectedBuilder && selectedBuilder === name}
    >
      <StyledCardBody
        id={`create-case-action-modal__card-body-${name}`}
        data-test={`create-case-action-modal__card-body-${name}`}
        onClick={onClick}
      >
        <BuilderIcon>{icon}</BuilderIcon>
        <ContentWrapper>
          <Card.Title data-test={`create-case-action-modal__card-body-title-${name}`}>
            {title}
          </Card.Title>
          <Paragraph>{description}</Paragraph>
        </ContentWrapper>
      </StyledCardBody>
    </StyledCard>
  );
};

interface CreateCaseActionModalFooterProps {
  selectedBuilder?: string;
  handleCreateCaseAction: (builder?: string) => Promise<void>;
}

const CreateCaseActionModalFooter = ({
  selectedBuilder,
  handleCreateCaseAction,
}: CreateCaseActionModalFooterProps) => {
  return (
    <PageFooter>
      <ConfirmationButton
        id='create-case-action-modal__start-from-scratch'
        data-test='create-case-action-modal__start-from-scratch'
        onClick={() => handleCreateCaseAction()}
        variant='basic'
      >
        Start from scratch
      </ConfirmationButton>
      <ConfirmationButton
        id='create-case-action-modal__use-prebuilt-action'
        data-test='create-case-action-modal__use-prebuilt-action'
        onClick={() => handleCreateCaseAction(selectedBuilder)}
        disabled={!selectedBuilder}
        variant='primary'
      >
        Use pre-built action
      </ConfirmationButton>
    </PageFooter>
  );
};

export default CreateCaseActionModal;
